import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import moment from "moment";
import "../styles/noPrint.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faEdit } from "@fortawesome/free-solid-svg-icons";

const ApproveReceiptVoucher = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const personId = searchParams.get("personId");
  const navigate = useNavigate();
  const receiptData = location.state?.data;

  const [data, setData] = useState({});
  const [rowData, setRowData] = useState(receiptData || {});
  const [selectedItems, setSelectedItems] = useState([]);
  const [inputDisabledList, setInputDisabledList] = useState([]);

  const token = localStorage.getItem("token");
  const centerCode = localStorage.getItem("code");
  const sewadarId = localStorage.getItem("sewadarId");
  const role = localStorage.getItem("role");
  const sewadarOf = localStorage?.getItem("sewadarOf");

  // Fetch center info (same as your original)
  useEffect(() => {
    const fetchCenterInfo = async () => {
      try {
        const response = await apiClient.get(`${SERVER_BASE}/centerInfoForDC`, {
          params: { code: centerCode },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(response.data[0]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchCenterInfo();
  }, []);

  // Initialize / recompute inputDisabledList when rowData or department changes
  useEffect(() => {
    const filteredItems =
      (rowData?.items || []).filter(
        (item) => item.departmentName === sewadarOf || role =='Authorized Person'
      ) || [];
    setInputDisabledList(filteredItems.map((item) => !!item.receivedQty));
  }, [rowData, sewadarOf]);

  if (!rowData) return <p>No data available. Please go back to the list.</p>;

  // Only show items belonging to this sewadar's department
  const itemDetails =
    (rowData?.items || []).filter(
      (item) => item.departmentName == sewadarOf || role =='Authorized Person'
    ) || [];

  const getApprovalSummary = () => {
    const summaryMap = new Map();
    rowData?.items?.forEach((item) => {
      const key = `${item.departmentName}|${item.approvedByName}`;
      if (!summaryMap.has(key)) {
        summaryMap.set(key, {
          departmentName: item.departmentName,
          approvedByName: item.approvedByName,
          approvedAt: item.approvedAt,
          count: 0,
        });
      }
      summaryMap.get(key).count += 1;
    });
    return Array.from(summaryMap.values());
  };

  const handleSaveAndApprove = async () => {
    if (selectedItems.length === 0) {
      return Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please select at least one item",
        timer: 2500,
        showConfirmButton: false,
      });
    }
  
    // Only selected rows
    const itemsToApprove = itemDetails.filter(item =>
      selectedItems.includes(item.dbId)
    );
  
    // Validation only for selected rows
    const missing = itemsToApprove.find(
      item => !item.receivedQty || Number(item.receivedQty) === 0
    );
  
    if (missing) {
      return Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Enter Received Qty for selected rows",
        timer: 2500,
        showConfirmButton: false,
      });
    }
  
    try {
      // 🔥 BUILD rowData CONTAINING ONLY SELECTED ITEMS
      const filteredRowData = {
        ...rowData,
        items: itemsToApprove // ONLY SELECTED ITEMS
      };
  
      const payload = {
        voucherId: rowData.id,
        approvedBy: sewadarId,
        rowData: filteredRowData
      };
  
      await apiClient.put(`${SERVER_BASE}/update-received-qty`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Selected items approved successfully",
        timer: 2500,
        showConfirmButton: false,
      });
  
      navigate(-1);
    } catch (error) {
      Swal.fire("Error", "Approval failed", "error");
    }
  };
  
  

  const approvalSummary = getApprovalSummary();

  return (
    <div
      style={{
        border: "1px solid",
        margin: "15px auto",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "1100px",
        fontSize: "14px",
      }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-secondary no-print"
          onClick={() => window.history.back()}
        >
          Back
        </button>
        <h3 className="m-0 text-center">Receipt</h3>
        <button
          className="btn btn-success no-print"
          onClick={() => handleSaveAndApprove()}
        >
          Approve Selected
        </button>
      </div>

      {/* Primary Info */}
      <div className="row mt-4" style={{ fontSize: "15px" }}>
        {rowData?.voucherType !='DR' && (
        <div className="col-6">
          <strong>GSRS No:</strong> {rowData?.gsrsNo}
        </div>
        )}
        <div className="col-6">
          <strong>Bill Date:</strong>{" "}
          {rowData?.date ? moment(rowData.date).format("DD-MMM-YY") : ""}
        </div>
        <div className="col-6">
          <strong>Document No:</strong> {rowData?.documentNo}
        </div>
        <div className="col-6">
          <strong>Document Date:</strong>{" "}
          {rowData?.documentDate
            ? moment(rowData.documentDate).format("DD-MMM-YY")
            : ""}
        </div>
        <div className="col-6">
          <strong>Ledger:</strong> {rowData?.ledgerName}
        </div>
        <div className="col-6">
          <strong>Purpose:</strong> {rowData?.purposeName}
        </div>
        <div className="col-6">
          <strong>On Account:</strong> {rowData?.onAccount}
        </div>
        {rowData?.onAccount != "Project" && (
          <div className="col-6">
            <strong>Inventory:</strong> {rowData?.inventory}
          </div>
        )}
         {rowData?.voucherType !='DR' && (<>
        <div className="col-6">
          <strong>Vehicle No:</strong> {rowData?.vehicleNo}
        </div>
        <div className="col-6">
          <strong>Driver Name:</strong> {rowData?.driverName}
        </div>
        <div className="col-6">
          <strong>Mobile No:</strong> {rowData?.driverMobile}
        </div>
        <div className="col-6">
          <strong>Entry Date & Time:</strong>{" "}
          {rowData?.entryDate
            ? moment(rowData.entryDate).format("DD-MMM-YYYY")
            : ""}{" "}
          {rowData?.entryTime
            ? moment(rowData.entryTime, "HH:mm:ss").format("hh:mm A")
            : ""}
        </div>
        </>
         )}
      </div>

      {/* Item Table */}
      <h5 className="mt-4 mb-2">Item Details</h5>
      <table className="table table-bordered" style={{ fontSize: "14px" }}>
        <thead className="table-light">
          <tr>
            <th>S.No</th>
            <th>Deptt.</th>
            <th>Item</th>
            <th>Document Qty</th>
            <th>Received QTY</th>
            <th>UOM</th>
            <th>Diff.</th>
            <th>Remarks</th>
            <th style={{ whiteSpace: "nowrap" }}>
  #&nbsp;&nbsp;
  <input
    type="checkbox"
    onChange={(e) => {
      if (e.target.checked) {
        const selectableIds = itemDetails
          .filter(item => !item.approvedBy)
          .map(item => item.dbId);
        setSelectedItems(selectableIds);
      } else {
        setSelectedItems([]);
      }
    }}
    checked={
      itemDetails.length > 0 &&
      itemDetails
        .filter(item => !item.approvedBy)
        .every(item => selectedItems.includes(item.dbId))
    }
  />
</th>

          </tr>
        </thead>
        <tbody>
          {itemDetails.map((item, index) => {
            const canEditRole =
              sewadarOf == item.departmentName &&
              (["Approver", "Main Incharge"].includes(role)) ||
                ["Authorized Person"].includes(role);

            const inputDisabled = inputDisabledList[index] ?? false;
            const isPrefilled = Number(item.receivedQty) > 0;
            const alreadyApproved = !!item.approvedBy;

            return (
              <tr key={index}>
                {/* Checkbox column */}
            

                <td>{index + 1}</td>
                <td>{item.departmentName}</td>
                <td>{item.itemName}</td>
                <td>
                  {Number(item.challanQty) % 1 === 0
                    ? parseInt(item.challanQty)
                    : parseFloat(item.challanQty).toFixed(2)}
                </td>

                <td style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: "80px" }}
                    value={item.receivedQty}
                    onChange={(e) => {
                      const newItems = [...(rowData.items || [])];
                      const originalIndex = newItems.findIndex(
                        (x) => x.dbId === item.dbId
                      );
                      if (originalIndex !== -1) {
                        newItems[originalIndex].receivedQty = e.target.value;
                        setRowData({ ...rowData, items: newItems });
                      }
                    }}
                    disabled={
                      alreadyApproved || inputDisabled || !canEditRole
                    }
                  />

                  {/* Show edit icon only if prefilled */}
                  {canEditRole && isPrefilled && !alreadyApproved && (
                    <FontAwesomeIcon
                      icon={faEdit}
                      style={{ cursor: "pointer", color: "green", fontSize: "18px" }}
                      onClick={() => {
                        const newDisabledList = [...inputDisabledList];
                        newDisabledList[index] = !newDisabledList[index];
                        setInputDisabledList(newDisabledList);
                      }}
                    />
                  )}
                </td>

                <td>{item.uom}</td>
                <td>
                  {Number(item.receivedQty) === Number(item.challanQty)
                    ? 0
                    : Number(item.receivedQty) < Number(item.challanQty)
                    ? `Short ${(
                        Number(item.challanQty) - Number(item.receivedQty)
                      ).toFixed(2)}`
                    : `Excess ${(
                        Number(item.receivedQty) - Number(item.challanQty)
                      ).toFixed(2)}`}
                </td>
                <td>{item.remarks}</td>
                <td>
                  {canEditRole && (
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.dbId)}
                      disabled={alreadyApproved}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems([...selectedItems, item.dbId]);
                        } else {
                          setSelectedItems(
                            selectedItems.filter((id) => id !== item.dbId)
                          );
                        }
                      }}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Approval Summary */}
      <h5 className="mt-5 mb-2">Approval Summary</h5>
      <table className="table table-bordered" style={{ fontSize: "14px" }}>
        <thead className="table-light">
          <tr>
            <th>S.No</th>
            <th>Department</th>
            <th>Approved By</th>
            <th>No. of Items</th>
          </tr>
        </thead>
        <tbody>
          {approvalSummary.map((summary, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{summary.departmentName}</td>
              <td>
                {summary.approvedByName
                  ? `${summary.approvedByName} ON ${
                      summary.approvedAt
                        ? moment(summary.approvedAt).format(
                            "DD-MMM-YYYY hh:mm A"
                          )
                        : ""
                    }`
                  : ""}
              </td>
              <td>{summary.count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div>
        <p
          className="text-end mb-4"
          style={{ marginBottom: "7px" }}
        >
          Radha Soami Satsang Beas
        </p>
        <p className="text-end" style={{ fontWeight: "bold" }}>
          Authorised Signatory
        </p>
      </div>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            .footer {
              position: absolute;
              bottom: 0;
              width: 100%;
              page-break-before: always;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ApproveReceiptVoucher;
